import React, { useCallback, useEffect, useRef, useState } from 'react';



declare global {

  interface Window {

    google?: {

      accounts: {

        id: {

          initialize: (config: {

            client_id: string;

            callback: (response: { credential?: string }) => void;

          }) => void;

          renderButton: (

            parent: HTMLElement,

            options: Record<string, string | number>,

          ) => void;

        };

      };

    };

  }

}



type Props = {

  clientId: string;

  label: string;

  disabled?: boolean;

  onSuccess: (credential: string) => void;

  onError?: () => void;

};



function GoogleGlyph({ className }: { className?: string }) {

  return (

    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">

      <path

        fill="#4285F4"

        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"

      />

      <path

        fill="#34A853"

        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"

      />

      <path

        fill="#FBBC05"

        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"

      />

      <path

        fill="#EA4335"

        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"

      />

    </svg>

  );

}



/** Custom-styled Google sign-in; uses hidden GIS button for id_token flow. */

export function GoogleSignInButton({ clientId, label, disabled, onSuccess, onError }: Props) {

  const hiddenHostRef = useRef<HTMLDivElement>(null);

  const callbackRef = useRef(onSuccess);

  const errorRef = useRef(onError);

  const [ready, setReady] = useState(false);



  callbackRef.current = onSuccess;

  errorRef.current = onError;



  useEffect(() => {

    if (!clientId || !hiddenHostRef.current) return;



    let cancelled = false;

    const host = hiddenHostRef.current;



    const mount = () => {

      if (cancelled || !host || !window.google?.accounts?.id) return false;

      host.innerHTML = '';

      window.google.accounts.id.initialize({

        client_id: clientId,

        callback: (response) => {

          if (response.credential) {

            callbackRef.current(response.credential);

          } else {

            errorRef.current?.();

          }

        },

      });

      window.google.accounts.id.renderButton(host, {

        type: 'standard',

        theme: 'outline',

        size: 'large',

        width: 320,

      });

      setReady(true);

      return true;

    };



    if (mount()) return () => { cancelled = true; };



    const timer = window.setInterval(() => {

      if (mount()) window.clearInterval(timer);

    }, 120);



    return () => {

      cancelled = true;

      window.clearInterval(timer);

    };

  }, [clientId]);



  const handleClick = useCallback(() => {

    const btn = hiddenHostRef.current?.querySelector('[role="button"]') as HTMLElement | null;

    if (!btn) {

      errorRef.current?.();

      return;

    }

    btn.click();

  }, []);



  return (

    <div className="relative w-full">

      <div

        ref={hiddenHostRef}

        className="absolute left-0 top-0 h-px w-px overflow-hidden opacity-0 pointer-events-none"

        aria-hidden="true"

      />

      <button

        type="button"

        onClick={handleClick}

        disabled={disabled || !ready}

        className="group relative w-full overflow-hidden rounded-full border border-[#dadce0] dark:border-slate-600 bg-white dark:bg-slate-800 px-5 py-3 text-sm font-medium text-[#3c4043] dark:text-slate-100 shadow-sm transition-all duration-200 hover:bg-[#f8f9fa] dark:hover:bg-slate-700/90 hover:shadow-md active:scale-[0.995] disabled:cursor-not-allowed disabled:opacity-55"

      >

        <span className="flex items-center justify-center gap-3">

          <GoogleGlyph className="h-[18px] w-[18px] shrink-0" />

          <span className="truncate tracking-tight">{label}</span>

        </span>

      </button>

    </div>

  );

}



export default GoogleSignInButton;

